// React imports
import React, { useState, useEffect, useMemo } from 'react';

// Generic Imports
import _ from 'lodash';
import { evaluate } from 'mathjs';
import { formulaFromLCLWM, parseLCLWMFormula } from '../../utils/Formula.js';
import { v4 as uuidv4 } from 'uuid';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faInfinity, faQuestion, faTrash, faStairs, faMoneyBill, faSymbols, faCalculator } from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';

// MUI Imports
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';

// Local imports
import { formulaToParams, formulaFromMinAndTiers } from '../../utils/Formula.js';
import currencyToText from '../../CurrencyToText.js';
import CurrencyTextField from './CurrencyTextField.js';

const selectedColor = '#605DEC';

const BASE_TIER = () => {
    return [{ id: uuidv4(), step: 0, value: 0, type: 'per-item' }, { id: uuidv4(), value: 0, type: 'per-item'}];
}

function FormulaEditorModal(props) {
    const { open, onClose, value: initialValue, onDone, sections, noTax, withBuyingMargin, initialTab, hideTabs, initialFixedOrTier, hideBuyingMarginOption, currency, organization, showLCLWM } = props;
    const [ tabValue, setTabValue ] = useState(initialTab !== undefined ? initialTab : (showLCLWM ? 3 : 0));
    const [ min, setMin ] = useState(0);
    const [ tiers, setTiers ] = useState(BASE_TIER());
    const [ fixed, setFixed ] = useState({ value: 0, type: 'per-item' });
    const [ fixedOrTier, setFixedorTier ] = useState('fixed');
    const [ tiersAcc, setTiersAcc ] = useState(true);
    const [ tax, setTax] = useState({ percentage: 10, fields: [] });
    const [ buyingMargin, setBuyingMargin] = useState('');
    const [ buyingMarginConstant, setBuyingMarginConstant] = useState('');
    const [ fixedAmountCurrency, setFixedAmountCurrency ] = useState(currency || 'USD');
    const [ fixedAmountFocused, setFixedAmountFocused ] = useState(null);
    const [ formula, setFormula ] = useState(initialValue);
    const [ cwFunc, setCwFunc ] = useState(null);
    const [ cwParams, setCwParams ] = useState(null);
    const [ x, setX ] = useState(1);
    const [ buyingX, setBuyingX ] = useState(1000);
    const [ error, setError ] = useState(false);
    const [ marginError, setMarginError ] = useState(false);
    // LCL W/M formula state
    const [ lclPerTon, setLclPerTon ] = useState('');
    const [ lclPerCbm, setLclPerCbm ] = useState('');
    const [ lclDensityRatio, setLclDensityRatio ] = useState('1000');
    const [ lclMinPerTon, setLclMinPerTon ] = useState('');
    const [ lclMinPerCbm, setLclMinPerCbm ] = useState('');

    const result = useMemo(() => {
        if (tabValue === 0) {
            const f = formulaFromMinAndTiers({
                min,
                tiers: fixedOrTier === 'tier' ? tiers : null,
                fixed: fixedOrTier === 'fixed' ? fixed : null,
                acc: tiersAcc,
                buying: fixedOrTier === 'buying-margin' ? {
                    value: (100 + parseFloat(buyingMargin)) / 100,
                    constant: parseFloat(buyingMarginConstant) || undefined
                } : null
            });
            if (!f) return null;
            try {
                const val = evaluate(f, { x, buying: 1000 }).toString()
                return { value: val };
            } catch (e) {
                return { error: true };
            }
        }

        if (tabValue === 3) {
            const perTon = parseFloat(lclPerTon) || 0;
            const perCbm = parseFloat(lclPerCbm) || 0;
            const dr = parseFloat(lclDensityRatio) || 1000;
            const mTon = parseFloat(lclMinPerTon) || 0;
            const mCbm = parseFloat(lclMinPerCbm) || 0;
            if (!perTon && !perCbm) return null;
            const f = formulaFromLCLWM({ perTon, perCbm, densityRatio: dr, minPerTon: mTon, minPerCbm: mCbm });
            try {
                const val = evaluate(f, { x: 0, buying: 0, cbm: 5, weightKg: 3000, tons: 3 }).toString();
                return { value: val, formula: f };
            } catch (e) {
                return { error: true };
            }
        }

        if (!formula) return null;
        try {
            const val = evaluate(formula, { x, buying: buyingX }).toString()
            return { value: val };
        } catch (e) {
            return { error: true };
        }
    }, [x, buyingX, formula, min, tiers, tiersAcc, tax, fixedOrTier, buyingMargin, buyingMarginConstant, tabValue, lclPerTon, lclPerCbm, lclDensityRatio, lclMinPerTon, lclMinPerCbm]);

    useEffect(() => {
        setTabValue(initialTab !== undefined ? initialTab : (showLCLWM ? 3 : 0));
        setMin(0);
        setTiers(BASE_TIER());
        setFixed({ value: 0, type: 'per-item' });
        setFixedorTier(initialFixedOrTier || 'fixed');
        setFormula('');
        setTax({ percentage: 10, fields: [] });
        setBuyingMargin('');
        setBuyingMarginConstant('');
        setFixedAmountCurrency(currency || 'USD');
        setFixedAmountFocused(null);
        setMarginError(false);

        if (open && initialValue) {
            if (initialValue.startsWith('sp')) {
                setTabValue(2);
                setCwFunc(initialValue.split('(')[0]);
                setCwParams(initialValue.split('(')[1].split(')')[0].split('\'')[1]);
            } else if (parseLCLWMFormula(initialValue)) {
                const parsed = parseLCLWMFormula(initialValue);
                setTabValue(3);
                setLclPerTon(parsed.perTon != null ? parsed.perTon.toString() : '');
                setLclPerCbm(parsed.perCbm != null ? parsed.perCbm.toString() : '');
                setLclDensityRatio(parsed.densityRatio != null ? parsed.densityRatio.toString() : '');
                setLclMinPerTon(parsed.minPerTon != null ? parsed.minPerTon.toString() : '');
                setLclMinPerCbm(parsed.minPerCbm != null ? parsed.minPerCbm.toString() : '');
            } else {
                const attempt = formulaToParams({ formula: initialValue });
                if (attempt === null) {
                    setTabValue(1);
                    setFormula(rewriteIdsToNames(initialValue));
                } else {
                    setTabValue(0);
                    setMin(attempt.minimum);
                    setTiers(attempt.tiers || BASE_TIER());
                    setTiersAcc(attempt.acc);
                    setFixed(attempt.fixed || { value: 0, type: 'per-item' });
                    setTax(attempt.tax || { percentage: 10, fields: [] });
                    setFixedorTier(attempt.buying ? 'buying-margin' : attempt.fixed ? 'fixed' : attempt.tiers ? 'tier' : 'tax');
                    setBuyingMargin(attempt.buying?.value ? ((attempt.buying.value - 1) * 100).toString() : '');
                    setBuyingMarginConstant(attempt.buying?.constant ? attempt.buying.constant.toString() : '');
                }
            }
        }
    }, [open]);

    function onOK() {
        // Validate margin is required when uplift mode is active, unless only fixed amount is provided
        let hasConstant = false;
        let hasMargin = false;
        if (hideTabs && initialFixedOrTier === 'buying-margin') {
            hasConstant = buyingMarginConstant && buyingMarginConstant.trim() !== '' && !isNaN(parseFloat(buyingMarginConstant));
            hasMargin = buyingMargin && buyingMargin.trim() !== '' && !isNaN(parseFloat(buyingMargin));
            
            // If only constant is provided, set margin to 0
            if (hasConstant && !hasMargin) {
                setBuyingMargin('0');
            } else if (!hasConstant && !hasMargin) {
                setMarginError(true);
                return;
            }
        }
        
        setMarginError(false);
        if (tabValue === 0) {
            // Use 0 for margin if only constant is provided
            const marginValue = (hasConstant && !hasMargin) ? '0' : buyingMargin;
            const f = formulaFromMinAndTiers({
                min,
                tiers: fixedOrTier === 'tier' ? tiers : null,
                fixed: fixedOrTier === 'fixed' ? fixed : null,
                tax: fixedOrTier === 'tax' ? tax : null,
                acc: tiersAcc,
                buying: fixedOrTier === 'buying-margin' ? { 
                    value: (100 + parseFloat(marginValue || '0')) / 100,
                    constant: parseFloat(buyingMarginConstant) || undefined
                } : null,
            });
            if (!f) return null;
            // If in buying-margin mode, pass currency along with formula
            if (fixedOrTier === 'buying-margin') {
                onDone({ formula: f, currency: fixedAmountCurrency });
            } else {
                onDone(f);
            }
        } else if (tabValue === 2) {
            onDone(`${cwFunc}('${cwParams}')`);
        } else if (tabValue === 3) {
            const perTon = parseFloat(lclPerTon) || 0;
            const perCbm = parseFloat(lclPerCbm) || 0;
            const dr = parseFloat(lclDensityRatio) || 1000;
            const mTon = parseFloat(lclMinPerTon) || 0;
            const mCbm = parseFloat(lclMinPerCbm) || 0;
            onDone(formulaFromLCLWM({ perTon, perCbm, densityRatio: dr, minPerTon: mTon, minPerCbm: mCbm }));
        } else {
            onDone(rewriteNamesToIds(formula));
        }
    }

    function handleClose(event, reason) {
        // Prevent closing via backdrop/ESC if margin is required but not entered
        // Allow closing if only fixed amount is provided (margin will be set to 0)
        if (reason && hideTabs && initialFixedOrTier === 'buying-margin') {
            const hasConstant = buyingMarginConstant && buyingMarginConstant.trim() !== '' && !isNaN(parseFloat(buyingMarginConstant));
            const hasMargin = buyingMargin && buyingMargin.trim() !== '' && !isNaN(parseFloat(buyingMargin));
            if (!hasConstant && !hasMargin) {
                setMarginError(true);
                return;
            }
        }
        setMarginError(false);
        onClose();
    }

    function handleCancel() {
        // Cancel button should always close, bypassing validation
        setMarginError(false);
        onClose();
    }

    function onEraseFormula() {
        onDone(null);
    }

    useEffect(() => {
        setFormula(rewriteIdsToNames(initialValue));
        setX(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialValue]);

    const taxOptions = useMemo(() => {
        return _.flatten(sections?.map(section => {
            return _.flatten((section.offers || [{ fields: section.fields }]).map((offer, offerIndex) => { // Support for templates as well, which don't have offers
                return _.compact(offer.fields.map(field => {
                    if (field.type === 'string') return null;
                    if (field.type === 'date') return null;
                    if (section.title) {
                        return { label: `${field.title || 'Unnamed Field'} (${section.title}${(section.offers || []).length > 1 ? ', offer ' + (offerIndex + 1) : ''})`, id: field.id };
                    } else {
                        return { label: `${field.title || 'Unnamed Field'}${(section.offers || []).length > 1 ? '(offer ' + (offerIndex + 1) + ')' : ''}`, id: field.id };
                    }
                }));
            }));
        }));
    }, [sections]);

    // Flat list of all fields with id+title for friendly <Name> ↔ field_<id> rewrites
    // in the Advanced-tab textarea. First name wins when duplicates exist.
    const fieldNameMap = useMemo(() => {
        const byId = {};
        const byName = {};
        (sections || []).forEach(section => {
            (section.offers || [{ fields: section.fields }]).forEach(offer => {
                (offer.fields || []).forEach(field => {
                    if (!field?.id || !field.title) return;
                    byId[field.id] = field.title;
                    if (!byName[field.title.toLowerCase()]) byName[field.title.toLowerCase()] = field.id;
                });
            });
        });
        return { byId, byName };
    }, [sections]);

    // Replace every field_<uuid_with_underscores> token in `f` with <FieldTitle>.
    function rewriteIdsToNames(f) {
        if (!f) return f;
        return f.replace(/field_([a-f0-9]{8}_[a-f0-9]{4}_[a-f0-9]{4}_[a-f0-9]{4}_[a-f0-9]{12})/gi, (m, ident) => {
            const id = ident.replace(/_/g, '-');
            const title = fieldNameMap.byId[id];
            return title ? `<${title}>` : m;
        });
    }

    // Replace every <Name> token in `f` with field_<uuid_with_underscores>. Case-insensitive
    // name match; tokens with no matching field are left untouched so the user notices.
    function rewriteNamesToIds(f) {
        if (!f) return f;
        return f.replace(/<([^<>]+)>/g, (m, name) => {
            const id = fieldNameMap.byName[name.trim().toLowerCase()];
            return id ? `field_${id.replace(/-/g, '_')}` : m;
        });
    }

    const isMarginRequired = hideTabs && initialFixedOrTier === 'buying-margin';
    const hasMargin = buyingMargin && buyingMargin.trim() !== '' && !isNaN(parseFloat(buyingMargin));
    const hasConstant = buyingMarginConstant && buyingMarginConstant.trim() !== '' && !isNaN(parseFloat(buyingMarginConstant));
    const isMarginValid = hasMargin || hasConstant;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={hideTabs && initialFixedOrTier === 'buying-margin' ? '450px' : 'md'}
            fullWidth={!(hideTabs && initialFixedOrTier === 'buying-margin')}
            PaperProps={{ sx: { overflowY: 'visible' } }}
            sx={{ zIndex: 1000010 }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '24px 24px 0px 24px'
            }}>
                {hideTabs && initialFixedOrTier === 'buying-margin' ? (
                    <>
                        <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>
                            Uplift
                        </Typography>
                        <IconButton
                            size='small'
                            onClick={handleCancel}
                            sx={{
                                color: '#64748b',
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faXmark} style={{ fontSize: '14px' }} />
                        </IconButton>
                    </>
                ) : (
                    'Formula Editor'
                )}
            </DialogTitle>
            <DialogContent>
                {!hideTabs && (
                    <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                        {showLCLWM && <Tab label='LCL W/M' value={3} />}
                        <Tab label='Basic Formula Editor' value={0} />
                        <Tab label='Advanced Formula Editor' value={1} />
                        <Tab label='Cargowise Editor' value={2} />
                    </Tabs>
                )}
                {tabValue === 2 && (
                    <Box sx={{ marginTop: '24px' }}>
                        <FormControl fullWidth>
                            <InputLabel>{!cwFunc ? 'Pick Calculator' : 'Calculator'}</InputLabel>
                            <Select label={!cwFunc ? 'Pick Calculator' : 'Calculator'} value={cwFunc} onChange={e => setCwFunc(e.target.value)} >
                                <MenuItem value={'sp_flt'}>Flat Calculator</MenuItem>
                                <MenuItem value={'sp_unt'}>Unit Calculator</MenuItem>
                                <MenuItem value={'sp_agy'}>Agency Calculator</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                )}
                {tabValue === 2 && cwFunc === 'sp_flt' && (
                    <Box sx={{ marginTop: '24px' }}>
                        <CargowiseFLTCalculator 
                            params={cwParams}
                            setParams={setCwParams}
                        />
                    </Box>
                )}
                {tabValue === 2 && cwFunc === 'sp_unt' && (
                    <Box sx={{ marginTop: '24px' }}>
                        <CargowiseUNTCalculator 
                            params={cwParams}
                            setParams={setCwParams}
                        />
                    </Box>
                )}
                {tabValue === 2 && cwFunc === 'sp_agy' && (
                    <Box sx={{ marginTop: '24px' }}>
                        <CargowiseAGYCalculator 
                            params={cwParams}
                            setParams={setCwParams}
                        />
                    </Box>
                )}
                {tabValue === 3 && (
                    <Box sx={{ marginTop: '24px' }}>
                        <Typography variant='body2' sx={{ mb: 2, color: '#64748b' }}>
                            Set different rates for weight and volume. The system will automatically choose based on the density ratio.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label='Amount per Ton'
                                type='number'
                                value={lclPerTon}
                                onChange={(e) => setLclPerTon(e.target.value)}
                                autoFocus
                            />
                            <TextField
                                fullWidth
                                label='Amount per CBM'
                                type='number'
                                value={lclPerCbm}
                                onChange={(e) => setLclPerCbm(e.target.value)}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label='Minimum per Ton'
                                type='number'
                                value={lclMinPerTon}
                                onChange={(e) => setLclMinPerTon(e.target.value)}
                                placeholder='0'
                                helperText='Optional'
                            />
                            <TextField
                                fullWidth
                                label='Minimum per CBM'
                                type='number'
                                value={lclMinPerCbm}
                                onChange={(e) => setLclMinPerCbm(e.target.value)}
                                placeholder='0'
                                helperText='Optional'
                            />
                        </Box>
                        {result && !result.error && (
                            <Alert severity='info' sx={{ mb: 1 }}>
                                Preview (5 cbm, 3 tons): <b>{currency ? `${currency} ` : ''}{result.value}</b>
                                <br />
                                <Typography variant='caption' sx={{ color: '#64748b' }}>
                                    Picks the higher of: per-cbm total vs per-ton total
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                )}
                {tabValue === 1 && (
                    <Box sx={{ marginTop: '24px' }}>
                        <DialogContentText sx={{ paddingBottom: '12px' }}>
                            Write a formula used to calculate the rate price.
                        </DialogContentText>
                        <DialogContentText sx={{ paddingBottom: '24px' }}>
                            Variable "x" will hold the number of containers, or the total chargeable weight of the package.
                        </DialogContentText>
                        <TextField
                            value={formula}
                            onChange={e => setFormula(e.target.value)}
                            label={'Formula'}
                            fullWidth
                            helperText={'Reference another field by its name in angle brackets, e.g. <THC> or <Origin Charges>. Saved as field_<id> automatically.'}
                        />
                        <Alert severity='info' sx={{ marginTop: '12px', fontSize: '11px' }}>
                            Some helpful formulas:
                            <div style={{ marginTop: '14px' }}>
                                <div><b>x * 0.2</b></div>
                                <div>Charge $0.2 for each kg.</div>
                            </div>
                            <div style={{ marginTop: '14px' }}>
                                <div><b>max(x * 0.2, 500)</b></div>
                                <div>Charge $0.2 for each kg, with a minimum of $500.</div>
                            </div>
                            <div style={{ marginTop: '14px' }}>
                                <div><b>x {'>'} 200 ? (0.2 * x) : x {'>'} 100 ? (0.3 * x) : (0.4 * x)</b></div>
                                <div>Charge $0.2 per kg if more than 200kg, or $0.3 per kg if more than 100kg, or $0.4 per kg.</div>
                            </div>
                            <div style={{ marginTop: '14px' }}>
                                <div><b>min(max((buying) * (1 - (buying {'<='} 180 ? 0 : buying {'<='} 250 ? 0.80 : buying {'<='} 350 ? 0.70 : buying {'<='} 500 ? 0.60 : 0.50)), buying - 300), buying - 20)</b></div>
                                <div>Tiered margin off the buying cost, capped between buying−300 and buying−20.</div>
                            </div>
                        </Alert>
                        <DialogTitle>
                            Test your formula
                        </DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box>x&nbsp;=&nbsp;</Box>
                            <TextField value={x} onChange={e => setX(e.target.value)}/>
                            <Box sx={{ marginLeft: '36px' }}>result&nbsp;=&nbsp;</Box>
                            <TextField value={(result || {}).value || ''} disabled/>
                        </Box>
                        {/\bbuying\b/.test(formula || '') && (
                            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '6px' }}>
                                <Box>buying&nbsp;=&nbsp;</Box>
                                <TextField value={buyingX} onChange={e => setBuyingX(e.target.value)}/>
                            </Box>
                        )}
                        <Alert severity='error' sx={{ visibility: (result || {}).error ? '' : 'hidden', marginTop: '6px' }}>
                            Error in formula or x.
                        </Alert>
                    </Box>
                )}
                {tabValue === 0 && (
                    <Box sx={{ marginTop: '24px' }}>
                        {!(hideTabs && initialFixedOrTier === 'buying-margin') && (
                            <DialogContentText sx={{ paddingBottom: '24px' }}>
                                Use our wizard to easily implement a formula for your rate.
                            </DialogContentText>
                        )}
                        {!(hideTabs && initialFixedOrTier === 'buying-margin') && (
                            <>
                                <DialogContentText sx={{ padding: '0px 0px 12px 0px', fontWeight: 800 }}>
                                    Do you have a minimum price?
                                </DialogContentText>
                                <TextField
                                    value={min}
                                    onChange={e => setMin(e.target.value)}
                                    fullWidth
                                />

                                <DialogContentText sx={{ padding: '24px 0px 12px 0px', fontWeight: 800 }}>
                                    Which pricing method are you using?
                                </DialogContentText>
                            </>
                        )}

                        {!(hideTabs && initialFixedOrTier === 'buying-margin') && (
                            <Box sx={{ flex: 1, border: '1px solid #CBD4E6', boxShadow: 'inset 0px -1px 0px #CBD4E6', display: 'flex' }}>
                                {_.compact(['fixed', 'tier', noTax && fixedOrTier !== 'tax' ? null : 'tax', (hideBuyingMarginOption ? null : (withBuyingMargin || (fixedOrTier === 'buying-margin') ? 'buying-margin' : null))]).map(mode => (
                                    <Box
                                        key={mode}
                                        sx={{
                                            ...(hideTabs && initialFixedOrTier && mode !== initialFixedOrTier ? { display: 'none' } : {}),
                                            textAlign: 'center',
                                            flex: 1,
                                            padding: '12px 24px',
                                            backgroundColor: mode === fixedOrTier ? selectedColor : '#F6F6FE',
                                            cursor: 'pointer',
                                            borderRight: '1px solid #CBD4E6'
                                        }}
                                        onClick={() => { if (!hideTabs || !initialFixedOrTier) setFixedorTier(mode) }}
                                    >
                                        <Typography variant={'body1'} style={{ color: mode === fixedOrTier ? 'white' : '#27273F' }}>
                                            <FontAwesomeIcon 
                                                icon={
                                                    mode === 'fixed' ? faMoneyBill : 
                                                    mode === 'tier' ? faStairs : 
                                                    mode === 'buying-margin' ? faCalculator : 
                                                    faSymbols
                                                } />
                                            &nbsp;&nbsp;
                                            {_.startCase(mode)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {fixedOrTier === 'tax' && (
                            <Box sx={{ marginTop: '24px', display: 'flex', alignItems: 'center' }}>
                                Calculate&nbsp;
                                <TextField 
                                    sx={{ width: '50px' }} 
                                    value={tax.percentage}
                                    onChange={e => setTax(t => ({ ...t, percentage: e.target.value }))}
                                />
                                % of the sum of the following fields:&nbsp;
                                <Autocomplete
                                    multiple
                                    disablePortal
                                    options={taxOptions}
                                    value={_.compact(tax.fields.map(id => taxOptions.find(o => o.id === id)))}
                                    onChange={(e, v) => setTax(t => ({ ...t, fields: v.map(vv => vv.id) }))}
                                    sx={{ minWidth: 300, flex: 1 }}
                                    renderInput={(params) => (
                                        <TextField {...params} />
                                    )}
                                />
                            </Box>
                        )}

                        {fixedOrTier === 'buying-margin' && (
                            <Box sx={{ marginTop: '24px', padding: '0px 24px' }}>
                                <DialogContentText sx={{ 
                                    paddingBottom: '20px',
                                    fontSize: '13px',
                                    color: '#64748b',
                                    margin: 0
                                }}>
                                    Add the uplift you want to apply to your costs.
                                </DialogContentText>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    <Box>
                                        <Typography sx={{ marginBottom: '8px', color: 'rgba(0, 0, 0, 0.87)', fontSize: '14px', fontWeight: 400 }}>Uplift (%)</Typography>
                                        <TextField 
                                            fullWidth
                                            value={buyingMargin}
                                            onChange={e => {
                                                setBuyingMargin(e.target.value);
                                                setMarginError(false);
                                            }}
                                            error={marginError}
                                            helperText={marginError ? 'Margin is required' : ''}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment 
                                                        position='end' 
                                                        sx={{ 
                                                            backgroundColor: 'transparent !important',
                                                            paddingRight: '14px',
                                                            height: '40px',
                                                            marginRight: '0px',
                                                            '& .MuiTypography-root': {
                                                                color: 'rgba(0, 0, 0, 0.87)'
                                                            }
                                                        }}
                                                    >
                                                        %
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: '#ffffff',
                                                    height: '40px',
                                                    '& fieldset': {
                                                        borderColor: '#e2e8f0',
                                                        borderWidth: '1px'
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#3b82f6',
                                                        borderWidth: '1px'
                                                    },
                                                    '&.Mui-error fieldset': {
                                                        borderColor: '#ef4444'
                                                    },
                                                    '& .MuiInputAdornment-root': {
                                                        backgroundColor: 'transparent !important'
                                                    }
                                                },
                                                '& .MuiInputBase-input': {
                                                    padding: '8px 14px',
                                                    height: '40px',
                                                    boxSizing: 'border-box'
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ marginBottom: '8px', color: 'rgba(0, 0, 0, 0.87)', fontSize: '14px', fontWeight: 400 }}>Fixed Amount</Typography>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'stretch',
                                            height: '40px',
                                            '& .MuiTextField-root': {
                                                flex: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: '#ffffff',
                                                    height: '40px',
                                                    borderRadius: '6px 0px 0px 6px',
                                                    '& fieldset': {
                                                        borderColor: '#e2e8f0',
                                                        borderWidth: '1px',
                                                        borderRight: 'none'
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#3b82f6',
                                                        borderWidth: '1px',
                                                        borderRight: 'none'
                                                    },
                                                    '&.Mui-error fieldset': {
                                                        borderColor: '#ef4444',
                                                        borderRight: 'none'
                                                    }
                                                },
                                                '& .MuiInputBase-input': {
                                                    padding: '8px 14px',
                                                    height: '40px',
                                                    boxSizing: 'border-box'
                                                }
                                            },
                                            '& button': {
                                                backgroundColor: '#f0f9ff',
                                                border: '1px solid #e2e8f0',
                                                borderLeft: 'none',
                                                borderRadius: '0px 6px 6px 0px',
                                                height: '40px',
                                                minHeight: '40px',
                                                '&:hover': {
                                                    backgroundColor: '#e0f2fe'
                                                }
                                            }
                                        }}>
                                            <CurrencyTextField
                                                value={buyingMarginConstant}
                                                setValue={setBuyingMarginConstant}
                                                focused={fixedAmountFocused}
                                                setFocused={setFixedAmountFocused}
                                                focusedId='fixed-amount'
                                                currency={fixedAmountCurrency}
                                                setCurrency={setFixedAmountCurrency}
                                                onBlur={() => {
                                                    // If only fixed amount is provided, allow submission (onOK will handle setting margin to 0)
                                                    if (buyingMarginConstant && buyingMarginConstant.trim() !== '' && !isNaN(parseFloat(buyingMarginConstant))) {
                                                        // Optionally trigger onOK here if needed
                                                    }
                                                }}
                                                disabled={false}
                                                organization={organization || { defaults: { currency: currency || 'USD' } }}
                                                forceTextField={true}
                                                width='100%'
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {fixedOrTier === 'fixed' && (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            Price
                                        </TableCell>
                                        <TableCell>
                                            Per (item or flat)
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                                            <TextField
                                                value={fixed.value}
                                                onChange={e => setFixed({ ...fixed, value: e.target.value })}
                                                sx={{ margin: '0px 6px', width: '100px' }}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant='text'
                                            color='primary'
                                            sx={{ textTransform: 'none', padding: '0px', minWidth: '0px' }}
                                            onClick={e => setFixed({...fixed, type: fixed.type === 'flat' ? 'per-item' : 'flat' })}
                                        >
                                            {fixed.type === 'flat' ? 'Flat Rate' : 'Per Item'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </Table>
                        )}

                        {fixedOrTier === 'tier' && (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            From
                                        </TableCell>
                                        <TableCell>
                                            To (excluding)
                                        </TableCell>
                                        <TableCell>
                                            Price
                                        </TableCell>
                                        <TableCell>
                                            Per (item or flat)
                                        </TableCell>
                                        <TableCell>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                {tiers.map((tier, index) => (
                                    <TableRow key={tier.id}>
                                        <TableCell>
                                            <DialogContentText>
                                                {index === 0 ? '0' : tiers[index - 1].step}&nbsp;
                                            </DialogContentText>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                                                {index < tiers.length - 1 && (
                                                    <TextField
                                                        value={tier.step}
                                                        onChange={e => setTiers(_tiers => _tiers.map(t => tier.id !== t.id ? t : {...tier, step: e.target.value}))}
                                                        sx={{ margin: '0px 6px', width: '100px' }}
                                                    />
                                                )}
                                                {index === tiers.length - 1 && (
                                                    <FontAwesomeIcon icon={faInfinity} />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                                                <TextField
                                                    value={tier.value}
                                                    onChange={e => setTiers(_tiers => _tiers.map(t => tier.id !== t.id ? t : {...tier, value: e.target.value}))}
                                                    sx={{ margin: '0px 6px', width: '100px' }}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant='text'
                                                color='primary'
                                                sx={{ textTransform: 'none', padding: '0px', minWidth: '0px' }}
                                                onClick={e => setTiers(_tiers => _tiers.map(t => tier.id !== t.id ? t : {...tier, type: tier.type === 'flat' ? 'per-item' : 'flat' }))}
                                            >
                                                {tier.type === 'flat' ? 'Flat Rate' : 'Per Item'}
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            {index < tiers.length - 1 && tiers.length > 2 && (
                                                <IconButton
                                                    color='error' 
                                                    onClick={() => setTiers(t => {
                                                        if (t.length === 2) return t;
                                                        const tt = t.filter(tt => tt.id !== tier.id);
                                                        delete tt[tt.length - 1].step;
                                                        return tt;
                                                    })}>
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </Table>
                        )}
                        {fixedOrTier === 'tier' && (
                            <Button
                                variant='text'
                                color='primary'
                                sx={{ textTransform: 'none' }}
                                onClick={() => setTiers(t => {
                                    const tt = _.clone(t);
                                    const last = _.last(tt);
                                    last.step = 0;
                                    return [...tt, { id: uuidv4(), value: last.value, type: 'per-item' }];
                                })}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                &nbsp;
                                Add another tier
                            </Button>
                        )}
                        {fixedOrTier === 'tier' && (
                            <Box>
                                <FormControlLabel
                                    control={<Checkbox checked={tiersAcc} onChange={() => setTiersAcc(t => !t)} />}
                                    label={
                                        <Typography variant='body2'>
                                            Tiers are accumulative
                                            <Tooltip
                                                title={
                                                    <Typography variant='caption' component={Box} >
                                                        Accumulative meaning for each tier, add previous tiers first.<br/>For example, if your pricing is:<br/> * First 10 items = $10 per item.<br/> * More than 10 items = $5 per item.<br/>For 15 items, accumulative pricing means $125 (10 * $10 + 5 * $5), and non-accumulative pricing means $75 (15 * $5).
                                                    </Typography>
                                                }>
                                                <span style={{ fontSize: '11px', padding: '3px', position: 'relative', top: '-3px' }} >
                                                    <FontAwesomeIcon icon={faQuestion} />
                                                </span>
                                            </Tooltip>
                                        </Typography>}
                                />
                                <Box sx={{ mt: 0.5, p: 1.5, borderRadius: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                    <Typography variant='caption' component='div' sx={{ color: '#475569', lineHeight: 1.6 }}>
                                        <strong>On</strong> — each tier's rate applies only to the units that fall inside that tier's range, and the tiers stack (like tax brackets).<br />
                                        <strong>Off</strong> — the single tier your amount lands in is applied to the whole amount.
                                    </Typography>
                                    <Typography variant='caption' component='div' sx={{ color: '#475569', lineHeight: 1.6, mt: 1 }}>
                                        <strong>Example</strong> — first 10 items @ $10, above 10 @ $5. For <strong>x&nbsp;=&nbsp;15</strong>:<br />
                                        • Accumulative (on) → 10&times;$10 + 5&times;$5 = <strong>$125</strong><br />
                                        • Non-accumulative (off) → 15&times;$5 = <strong>$75</strong>
                                    </Typography>
                                    <Typography variant='caption' component='div' sx={{ mt: 1, fontWeight: 700, color: tiersAcc ? '#16a34a' : '#b45309' }}>
                                        Currently {tiersAcc ? 'ON — tiers stack (this example would give $125)' : 'OFF — one tier for the whole amount (this example would give $75)'}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        {(fixedOrTier === 'fixed' || fixedOrTier === 'tier') && (
                            <>
                                <Box sx={{ fontWeight: '800', padding: '24px 0px 12px 0px' }}>
                                    Test your formula
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box>x&nbsp;=&nbsp;</Box>
                                    <TextField value={x} onChange={e => setX(e.target.value)}/>
                                    <Box sx={{ marginLeft: '36px' }}>result&nbsp;=&nbsp;</Box>
                                    <TextField value={(result || {}).value || ''} disabled/>
                                </Box>
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={hideTabs && initialFixedOrTier === 'buying-margin' ? { padding: '16px 24px' } : {}}>
                {initialValue && (
                    <Button variant='text' color='error' onClick={onEraseFormula}>
                        Erase Formula
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                {hideTabs && initialFixedOrTier === 'buying-margin' ? (
                    <>
                        <Button 
                            variant='text' 
                            onClick={handleCancel}
                            sx={{
                                color: '#475569',
                                textTransform: 'none',
                                padding: '8px 16px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            CANCEL
                        </Button>
                        <Button 
                            variant='contained' 
                            color='primary' 
                            onClick={onOK} 
                            disabled={isMarginRequired && !isMarginValid}
                            sx={{
                                borderRadius: '6px',
                                textTransform: 'none',
                                padding: '8px 16px'
                            }}
                        >
                            ADD
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant='outlined' color='secondary' onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button variant='contained' color='primary' onClick={onOK} disabled={isMarginRequired && !isMarginValid}>
                            OK
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default FormulaEditorModal;

function CargowiseFLTCalculator(props) {
    const { params, setParams } = props;
    const [ basePrice, setBasePrice ] = useState(0);

    const json = useMemo(() => JSON.parse(params || '{}'), [ params ]);

    useEffect(() => {
        setBasePrice(json.BasePrice || 0);
    }, [json]);

    return (
        <Box>
            <TextField
                label='Base Price'
                value={basePrice}
                onChange={e => setBasePrice(e.target.value)}
                onBlur={e => setParams(JSON.stringify({ BasePrice: parseFloat(basePrice) }))}
            />
        </Box>
    );
}

function CargowiseUNTCalculator(props) {
    const { params, setParams } = props;
    const [ perUnitPrice, setPerUnitPrice ] = useState(0);

    const json = useMemo(() => JSON.parse(params || '{}'), [ params ]);

    useEffect(() => {
        setPerUnitPrice(json.PerUnitPrice || 0);
    }, [json]);

    return (
        <Box>
            <TextField
                label='Per Unit Price'
                value={perUnitPrice}
                onChange={e => setPerUnitPrice(e.target.value)}
                onBlur={e => setParams(JSON.stringify({ PerUnitPrice: parseFloat(perUnitPrice) }))}
            />
        </Box>
    );
}

function CargowiseAGYCalculator(props) {
    const { params, setParams } = props;
    const [ rate, setRate ] = useState(0);
    const [ maximum, setMaximum ] = useState(0);

    const json = useMemo(() => JSON.parse(params || '{}'), [ params ]);

    useEffect(() => {
        setRate(json.Rate || 0);
        setMaximum(json.Maximum || 0);
    }, [json]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                    fullWidth
                    label='Rate'
                    value={rate}
                    onChange={e => setRate(e.target.value)}
                    onBlur={e => setParams(JSON.stringify({
                        ...params,
                        Rate: parseFloat(rate),
                    }))}
                />
                <TextField
                    fullWidth
                    label='Maximum'
                    value={maximum}
                    onChange={e => setMaximum(e.target.value)}
                    onBlur={e => setParams(JSON.stringify({
                        ...params,
                        Maximum: parseFloat(maximum)
                    }))}
                />
            </Box>
        </Box>
    );
}
